import './CartCheckoutTool.less';
import React, {useState, useEffect, useRef} from 'react';
import __ from '../../../i18n';
import Icon from '../../../components/icon/Icon';
import NumberInput from '../../../components/number-input/NumberInput';
import {breakLongWords} from '../../../utils/string';
import Button from '../../../components/button/Button';
import validatePhoneNumber from '../../validators/validatePhoneNumber';
import Request from "../../../api/Request";
import PurchaseOrderCreateParcel from "../../endpoints/purchase-order/PurchaseOrderCreateParcel";


export default function CartCheckoutTool({
    cartItems: fCartItems = [],
    updateItem = () => {
    },
    removeItem = () => {
    },
    clearCart = () => {
    },
    cancel = null,
    isPreviewMode = false,
    openFullMode = () => {
    },
    onExposeSetCartItems = (fn) => {},
    releaseSetCartItems = (fn) => {}
}) {
    const [cartItems, setCartItems] = useState(fCartItems);

    useEffect(() => {
        setCartItems(fCartItems);
    }, [fCartItems]);

    useEffect(() => {
        onExposeSetCartItems(setCartItems);

        return () => releaseSetCartItems(setCartItems);
    }, []);

    const totalQuantity = cartItems.reduce((result, item) => result + item.quantity, 0);
    const totalAmount = cartItems.reduce((result, item) => result + item.quantity * item.unit_price, 0);

    const formRef = useRef(null);

    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);

    const getContactInfo = () => {
        const {
            contact_name,
            contact_phone,
        } = formRef.current.elements;

        return {
            contact_name: contact_name.value,
            contact_phone: contact_phone.value,
        };
    };

    const getError = () => {
        if (cartItems.length === 0) {
            return __('Your cart is currently empty');
        }

        const {
            contact_name,
            contact_phone,
        } = getContactInfo();

        if (contact_name.trim().length === 0) {
            return __('Please enter your name');
        }

        if (contact_phone.trim().length === 0) {
            return __('Please enter your phone number');
        }

        if (!validatePhoneNumber(contact_phone)) {
            return __('Invalid phone number');
        }

        return null;
    };


    const submit = () => {
        if (isSubmitting) {
            return;
        }

        const error = getError();

        if (error !== null) {
            setError(error);
            return;
        }

        setError(null);
        setIsSubmitting(true);
        
        Request.add(new PurchaseOrderCreateParcel(getContactInfo(), cartItems))
            .then(() => {
                setIsSubmittedSuccessfully(true);
                clearCart();
            })
            .catch(error => {
                setError(error.getMessage());
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const [inputsForced, setInputsForced] = useState(false);

    useEffect(() => {
        setInputsForced(true);
    }, [cartItems]);

    useEffect(() => {
        if (inputsForced) {
            setInputsForced(false);
        }
    }, [inputsForced]);

    if (isSubmittedSuccessfully) {
        return <div className="cart-checkout-tool">
            <div className="heading">
                <div className="title">{__('Thank you!')}</div>
                {
                    cancel &&
                    <button
                        className="close-button"
                        type="button"
                        onClick={cancel}
                    >
                        <Icon name="times"/>
                    </button>
                }
            </div>
            <div className="success-report">
                <Icon name="tick"/>
                <div className="text">{__('Your order has been successfully sent')}</div>
            </div>
        </div>;
    }

    return <div className={`cart-checkout-tool ${isPreviewMode ? ' is-preview-mode' : ''}`}>
        <div className="heading">
            <div className="title">{__('Order information')}</div>
            {
                cancel &&
                <button
                    className="close-button"
                    type="button"
                    onClick={cancel}
                >
                    <Icon name="times"/>
                </button>
            }
        </div>
        <div className="body">
            <div className="content-area">
                {
                    cartItems.length > 0 &&
                    <div className="table-wrapper">
                        <table>
                            <thead>
                            <tr>
                                <td>{__('Product photo')}</td>
                                <td>{__('Product name')}</td>
                                <td>{__('Quantity')}</td>
                                <td>{__('Unit price')}</td>
                                <td>{__('Amount')}</td>
                                <td>{}</td>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                cartItems
                                    .map(item => {
                                        return <tr key={item.id}>
                                            <td>
                                                <img src={item.image_src}/>
                                            </td>
                                            <td>
                                                <a href={item.url} title={__('Open in new tab')} target="_blank">
                                                    {breakLongWords(item.name)}
                                                </a>
                                            </td>
                                            <td>
                                                <NumberInput
                                                    step={1}
                                                    min={1}
                                                    max={100}
                                                    defaultValue={item.quantity}
                                                    value={inputsForced ? item.quantity : undefined}
                                                    onChange={quantity => {
                                                        updateItem(item.id, {quantity});
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                {
                                                    item.is_price_public
                                                        ? __(':amount(CURRENCY)', {amount: item.unit_price})
                                                        : __('Contact')
                                                }
                                            </td>
                                            <td>
                                                {
                                                    item.is_price_public
                                                        ? __(':amount(CURRENCY)', {amount: item.unit_price * item.quantity})
                                                        : __('_')
                                                }
                                            </td>
                                            <td>
                                                <button
                                                    className="remove-button"
                                                    type="button"
                                                    onClick={() => removeItem(item.id)}
                                                >
                                                    <Icon name="times"/>
                                                </button>
                                            </td>
                                        </tr>;
                                    })
                            }
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colSpan={2}>
                                    {__('Total')}
                                </td>
                                <td>
                                    {__(':number', {number: totalQuantity})}
                                </td>
                                <td>{}</td>
                                <td>
                                    {
                                        cartItems.every(t => t.is_price_public)
                                            ? __(':amount(CURRENCY)', {amount: totalAmount})
                                            : __('_')
                                    }
                                </td>
                                <td>{}</td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                }
                {
                    cartItems.length === 0 &&
                    <div className="empty-notify">
                        {__('Your cart is currently empty')}
                    </div>
                }
            </div>

            {
                !isPreviewMode &&
                <form
                    className="contact-form"
                    ref={formRef}
                    onSubmit={ev => {
                        ev.preventDefault();
                        submit();
                    }}
                >
                    <div className="caption">
                        {__('Please leave your contact information, we will call you to confirm this order')}
                    </div>

                    <div className="form-control">
                        <label>{__('Your name')}</label>
                        <input
                            type="text"
                            name="contact_name"
                            placeholder={__('Enter your name')}
                        />
                    </div>

                    <div className="form-control">
                        <label>{__('Contact phone number')}</label>
                        <input
                            type="text"
                            name="contact_phone"
                            placeholder={__('Enter your phone number')}
                        />
                    </div>

                    <button type="submit" style={{display: 'none'}}/>
                </form>
            }

            {
                error !== null &&
                <div className="error-report">
                    <Icon name="caution-circle"/>
                    <div className="text">{error}</div>
                </div>
            }

            <div className="action-bar">
                {
                    cancel &&
                    <Button
                        name='cancel'
                        title={__('Cancel')}
                        onClick={cancel}
                        appearance="primary-text"
                        size="medium"
                    />
                }
                {
                    !isPreviewMode &&
                    <Button
                        name='purchase'
                        title={isSubmitting ? __('In progress...') : __('Purchase')}
                        disabled={isSubmitting}
                        onClick={submit}
                        appearance={isSubmitting ? 'primary-text' : 'primary'}
                        size="medium"
                    />
                }
                {
                    isPreviewMode &&
                    <Button
                        name='purchase'
                        title={__('Purchase')}
                        onClick={openFullMode}
                        appearance="primary"
                        size="large"
                    />
                }
            </div>
        </div>
    </div>;
}
